import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Radio, Square, Megaphone, Layers, Users, MessageSquare, Zap, Copy, Circle, Archive, MessageCircle, Save, Trash2, X, Video as VideoIcon, Scissors } from "lucide-react";
import { toast } from "sonner";
import LiveBroadcaster from "../components/LiveBroadcaster";
import AdvancedDirectorPanel from "../components/AdvancedDirectorPanel";
import DirectorMixerRack from "../components/DirectorMixerRack";
import { MobileTabSection, MobileBottomNav } from "../components/MobileTabBar";
import { Sliders, Image as ImageIcon, Mic, MicOff, RefreshCcw } from "lucide-react";
import PhoneCameraPanel from "../components/dj/PhoneCameraPanel";
import GuestManager from "../components/GuestManager";
import QrOverlayPanel, { QrOverlayLayer } from "../components/QrOverlayPanel";
import ThumbnailPicker from "../components/ThumbnailPicker";
import GoLiveShareBar from "../components/GoLiveShareBar";
import AiThumbnailPicker from "../components/AiThumbnailPicker";
import MobileLivePreviewBar from "../components/MobileLivePreviewBar";
import MobileLiveStudio from "../components/MobileLiveStudio";
import GoLiveTutorial, { tutorialHasBeenSeen } from "../components/GoLiveTutorial";
import StudioFxModal from "../components/StudioFxModal";
import useLivePreviewCapture from "../hooks/useLivePreviewCapture";

const SCENES = [
  { id: "camera", label: "Main Camera" },
  { id: "intro", label: "Intro Clip" },
  { id: "guest", label: "Guest Cam" },
  { id: "ad", label: "Ad Break" },
  { id: "brb", label: "Be Right Back" },
  { id: "outro", label: "Outro" },
];

export default function DirectorsMode() {
  const { user } = useAuth();
  const [streams, setStreams] = useState([]);
  const [active, setActive] = useState(null);
  const [chat, setChat] = useState([]);
  const [guests, setGuests] = useState([]);
  const [viewerCount, setViewerCount] = useState(0);
  const [genres, setGenres] = useState([]);
  const [newStreamTitle, setNewStreamTitle] = useState("");
  const [newStreamGenre, setNewStreamGenre] = useState("live_events");
  const [scheduleDate, setScheduleDate] = useState(""); // datetime-local value
  const [mine, setMine] = useState([]); // creator's scheduled + past streams
  const chatRef = useRef(null);

  // load genres
  useEffect(() => {
    api.get("/live-genres").then((r) => setGenres(r.data || [])).catch(() => {});
  }, []);

  // load own streams.
  // Don't auto-select a past stream — the user told us "started lives never
  // stop" because the previous behavior auto-resumed the most recent row,
  // re-opened the camera, and quietly kept it "live". Now: only resume a
  // CURRENTLY-LIVE stream owned by this user; otherwise show the create
  // form first so the host always sees + sets a title before broadcasting.
  useEffect(() => {
    api.get("/live").then((r) => {
      const mine = (r.data || []).filter((s) => s.creator_id === user.user_id);
      setStreams(mine);
      const stillLive = mine.find((s) => s.is_live);
      if (stillLive) setActive(stillLive); // honest resume of an in-progress live
    });
  }, [user.user_id]);

  // poll chat + guests + REAL viewer count from /api/live/{id}
  useEffect(() => {
    if (!active?.id) return;
    let stop = false;
    const tick = async () => {
      try {
        const [c, g, s] = await Promise.all([
          api.get(`/live/${active.id}/chat`),
          api.get(`/live/${active.id}/guests`).catch(() => ({ data: [] })),
          api.get(`/live/${active.id}`).catch(() => ({ data: null })),
        ]);
        if (stop) return;
        setChat(c.data);
        setGuests(g.data);
        if (s.data && typeof s.data.viewer_count === "number") {
          setViewerCount(s.data.viewer_count);
        }
        if (s.data) {
          // Mirror clips_shared_count + preview_url + thumbnail_url back into
          // the active stream doc so the badges/picker reflect server truth.
          setActive((a) => a && a.id === s.data.id ? {
            ...a,
            clips_shared_count: s.data.clips_shared_count || 0,
            preview_url: s.data.preview_url || a.preview_url,
            thumbnail_url: s.data.thumbnail_url || a.thumbnail_url,
            is_live: typeof s.data.is_live === "boolean" ? s.data.is_live : a.is_live,
          } : a);
        }
      } catch {}
    };
    tick();
    const h = setInterval(tick, 3500);
    return () => { stop = true; clearInterval(h); };
  }, [active?.id]);

  // Auto-capture a 5s hover-preview every 60s while live so the discovery
  // rail's homepage cards can play silent loops on hover.
  useLivePreviewCapture({ streamId: active?.id, isLive: !!active?.is_live, intervalSec: 60 });

  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [chat]);

  const [newStreamThumb, setNewStreamThumb] = useState("");

  const startStream = async () => {
    // Require an explicit title — the previous "Untitled Live" fallback
    // led to a flood of orphaned, unnamed streams when creators tapped
    // Start before typing anything. Force them to type a title so the
    // viewer-side card has real metadata.
    const title = (newStreamTitle || "").trim();
    if (!title) {
      toast.error("Give your stream a title before going live");
      // Try to focus the title input so the next keystroke lands.
      setTimeout(() => {
        const el = document.querySelector('[data-testid="stream-title-input"]');
        if (el && typeof el.focus === "function") el.focus();
      }, 30);
      return;
    }
    const { data } = await api.post("/live", {
      title,
      description: "",
      category: newStreamGenre,
      thumbnail_url: newStreamThumb || null,
    });
    setStreams([data, ...streams]);
    setActive(data);
    setNewStreamTitle("");
    setNewStreamThumb("");
    setWebrtcMode(true); // explicit: now they want the camera up
    toast.success(`You're live in ${newStreamGenre.replace("_", " ")}`);
  };

  // --- Schedule a future stream (doesn't go live yet). Uses the same POST /live
  //     endpoint, passing `scheduled_for` so the backend creates a status=scheduled doc.
  const scheduleStream = async () => {
    const title = (newStreamTitle || "").trim();
    if (!title) { toast.error("Stream title is required"); return; }
    if (!scheduleDate) { toast.error("Pick a date/time to schedule"); return; }
    const iso = new Date(scheduleDate).toISOString();
    if (new Date(iso) < new Date()) { toast.error("Pick a time in the future"); return; }
    try {
      await api.post("/live", {
        title,
        description: "",
        category: newStreamGenre,
        thumbnail_url: newStreamThumb || null,
        scheduled_for: iso,
      });
      toast.success(`Scheduled for ${new Date(iso).toLocaleString()}`);
      setNewStreamTitle("");
      setScheduleDate("");
      setNewStreamThumb("");
      await refreshMine();
      // IMPORTANT: do NOT push a scheduled (not-live-yet) stream into `streams`
      // — that list controls the broadcaster UI and triggers the "Select a
      // stream…" pane, which hides the just-rendered scheduled list. Creator
      // can kick it off via scheduled-stream-go-live-{id}.
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't schedule stream");
    }
  };

  const refreshMine = async () => {
    try {
      const { data } = await api.get("/live/mine/all");
      setMine(data || []);
    } catch {}
  };

  useEffect(() => { refreshMine(); }, []);

  const cancelScheduled = async (s) => {
    if (!window.confirm(`Cancel scheduled stream "${s.title}"?`)) return;
    try {
      await api.delete(`/live/${s.id}/schedule`);
      toast.info("Scheduled stream cancelled");
      await refreshMine();
      setStreams((prev) => prev.filter((x) => x.id !== s.id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't cancel");
    }
  };

  const goLiveScheduled = async (s) => {
    try {
      const { data } = await api.post(`/live/${s.id}/go-live`);
      toast.success(`Going live: ${s.title}`);
      await refreshMine();
      setStreams((prev) => {
        const without = prev.filter((x) => x.id !== s.id);
        return [data, ...without];
      });
      setActive(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start");
    }
  };

  const [endPrompt, setEndPrompt] = useState(null); // holds the stream awaiting end-action
  const [endBusy, setEndBusy] = useState("");

  // Studio FX (Person Cutout / Green Screen / Virtual Background) —
  // hydrated from same localStorage key as DirectorConsole so the host's
  // settings persist between mobile and desktop sessions.
  const [fxOpen, setFxOpen] = useState(false);
  const [fxSettings, setFxSettings] = useState(() => {
    try {
      const raw = localStorage.getItem("tapin_studio_fx");
      if (raw) return JSON.parse(raw);
    } catch { /* ignore corrupted blob */ }
    return null;
  });

  // First-run tutorial — pops once for new hosts, then on demand from the
  // empty state's "Learn how to go LIVE" button.
  const [tutorialOpen, setTutorialOpen] = useState(false);
  useEffect(() => {
    if (!tutorialHasBeenSeen()) {
      // Tiny delay so it lands AFTER the studio renders, not during mount.
      const t = setTimeout(() => setTutorialOpen(true), 700);
      return () => clearTimeout(t);
    }
    return undefined;
  }, []);

  // Viewport flag so we render the TikTok-style MobileLiveStudio takeover
  // on phones and the desktop two-column layout on lg+.
  const [isMobileFlow, setIsMobileFlow] = useState(() =>
    typeof window !== "undefined" && window.matchMedia
      ? !window.matchMedia("(min-width: 1024px)").matches
      : false,
  );
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(min-width: 1024px)");
    const onChange = () => setIsMobileFlow(!mq.matches);
    onChange();
    try { mq.addEventListener("change", onChange); }
    catch { mq.addListener(onChange); }
    return () => {
      try { mq.removeEventListener("change", onChange); }
      catch { mq.removeListener(onChange); }
    };
  }, []);

  // Default false so the camera doesn't open automatically on entry — the
  // user explicitly clicks "Start Camera & Broadcast" inside the page once
  // they've titled their stream. This prevents "lives that never stop"
  // (the previous default-true behavior auto-mounted the camera as soon as
  // any row was selected, even if the user only meant to manage a past
  // stream). For a stream that is actively live (is_live=true) we still
  // open the camera automatically — the host is mid-broadcast.
  const [webrtcMode, setWebrtcMode] = useState(false);
  // Independent flag so the user can dismiss the TikTok-style mobile
  // takeover to reach a sub-tab (Overlays / Audio / Guests / Chat) and
  // come back with one tap via the floating "Back to Studio" pill —
  // without losing the broadcast. Defaults to true so the takeover
  // opens automatically the moment the user starts the broadcast.
  const [mobileStudioOpen, setMobileStudioOpen] = useState(true);
  // Director audio rack hookup — AdvancedDirectorPanel publishes its
  // broadcaster proxy + AudioContext as soon as the WebRTC mix graph
  // exists. We grab them here so the bottom-docked DirectorMixerRack can
  // drive every fader / FX / Mic 2 picker for real (not just UI).
  const [broadcasterProxy, setBroadcasterProxy] = useState(null);
  const [audioPipe, setAudioPipe] = useState({ ctx: null, dest: null });
  const [mixerOpen, setMixerOpen] = useState(false);
  const [mixerFloat, setMixerFloat] = useState(false);
  const [liveGuests, setLiveGuests] = useState([]);
  // Mobile tab navigation — desktop (lg+) ignores this and shows
  // everything in its native two-column layout. On phones we surface a
  // sticky tab bar that swaps the visible section so the page is no
  // longer 5000px tall to scroll.
  const [mobileTab, setMobileTab] = useState("broadcast");
  // Auto-reopen the takeover whenever the user navigates back to the
  // "broadcast" tab from a sub-tab. Without this, the user lands on
  // the BroadcastTab without seeing the camera preview and gets
  // confused about where the live studio went.
  useEffect(() => {
    if (mobileTab === "broadcast") setMobileStudioOpen(true);
  }, [mobileTab]);
  // Mic mute state (driven by the quick-action sheet via setMicVol(0))
  const [micMuted, setMicMuted] = useState(false);
  const lastMicVolRef = useRef(1);
  const handleToggleMic = () => {
    if (!broadcasterProxy?.setMicVol) return;
    if (micMuted) {
      broadcasterProxy.setMicVol(lastMicVolRef.current || 1);
      setMicMuted(false);
    } else {
      broadcasterProxy.setMicVol(0);
      setMicMuted(true);
    }
  };
  // Lazy-fetch active guests so the rack channel labels show "Mic" etc on
  // the host side and "Guest 1 / 2 / …" once a guest is admitted.
  useEffect(() => {
    if (!active?.id) return undefined;
    const tick = () =>
      api.get(`/live/${active.id}/guests`)
        .then((r) => setLiveGuests((r.data || []).filter((g) => g.status === "approved" || g.status === "onscreen")))
        .catch(() => {});
    tick();
    const id = setInterval(tick, 6000);
    return () => clearInterval(id);
  }, [active?.id]);
  // Auto-flip ON when the host explicitly resumes a live stream (above
  // useEffect sets active=stillLive) so they don't have to re-click.
  useEffect(() => { if (active?.is_live) setWebrtcMode(true); }, [active?.id, active?.is_live]);

  // ─── "Lives that never stop" watchdog ────────────────────────────────
  // The user reported that broadcasts kept staying is_live=true after the
  // host closed the tab / hit back. Two complementary safeguards:
  //   1. beforeunload — warn the host when they try to leave with a live
  //      stream so they know to hit End Broadcast first.
  //   2. pagehide / unload — fire a best-effort POST /api/live/{id}/stop
  //      via navigator.sendBeacon so the row gets marked is_live=false
  //      even if the host force-quits the tab.
  // We only act when active.is_live to avoid stopping legitimate paused
  // streams the host is editing.
  useEffect(() => {
    if (!active?.is_live) return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "You're still live on Tap In TV. Are you sure you want to leave without ending the broadcast?";
      return e.returnValue;
    };
    const onPageHide = () => {
      try {
        // sendBeacon survives tab close where fetch() doesn't.
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/live/${active.id}/beacon-stop`;
        const token = localStorage.getItem("tv_token");
        const body = new Blob([JSON.stringify({ token })], { type: "application/json" });
        navigator.sendBeacon?.(url, body);
      } catch { /* swallow — best-effort */ }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      window.removeEventListener("pagehide", onPageHide);
    };
  }, [active?.id, active?.is_live]);
  const [chatCollapsed, setChatCollapsed] = useState(false);
  const [commentsEnabled, setCommentsEnabled] = useState(true);

  // Sync chat-enabled flag when the active stream changes
  useEffect(() => {
    if (active && typeof active.comments_enabled === "boolean") setCommentsEnabled(active.comments_enabled);
  }, [active?.id]);

  const toggleComments = async () => {
    if (!active) return;
    const next = !commentsEnabled;
    setCommentsEnabled(next);
    try {
      await api.put(`/live/${active.id}`, { comments_enabled: next });
      toast.success(next ? "Viewer comments turned ON" : "Viewer comments turned OFF");
    } catch (e) {
      setCommentsEnabled(!next);
      toast.error("Couldn't change comments setting");
    }
  };

  const stopStream = async () => {
    if (!active) return;
    await api.post(`/live/${active.id}/stop`);
    toast.info("Stream ended — choose what to do with the recording.");
    setEndPrompt(active);
  };

  const finishStream = async (action, extra = {}) => {
    if (!endPrompt) return;
    setEndBusy(action);
    try {
      const { data } = await api.post(`/live/${endPrompt.id}/finish`, { action, ...extra });
      if (action === "archive") toast.success("Replay saved to your library.");
      else if (action === "post_to_feed") toast.success("Recap posted to your community wall.");
      else if (action === "save_draft") toast.success("Stream saved as draft.");
      else if (action === "delete") toast.success("Stream deleted.");
      setStreams(streams.filter((s) => s.id !== endPrompt.id));
      setActive(null);
      setEndPrompt(null);
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't finish the stream");
    } finally {
      setEndBusy("");
    }
  };

  const switchScene = async (scene) => {
    await api.post(`/live/${active.id}/scene`, { scene });
    setActive({ ...active, scene });
    toast.success(`Scene: ${scene}`);
  };

  const triggerAd = async () => {
    await api.post(`/live/${active.id}/ad`, { duration_sec: 15 });
    toast.success("Ad break triggered (15s)");
  };

  const [msg, setMsg] = useState("");
  const sendChat = async (e) => {
    e.preventDefault();
    if (!msg.trim() || !active) return;
    await api.post(`/live/${active.id}/chat`, { message: msg });
    setMsg("");
  };

  const decide = async (g, decision) => {
    await api.post(`/live/${active.id}/guest/${g.id}/decision`, { decision });
    toast.success(`Guest ${decision}`);
    const res = await api.get(`/live/${active.id}/guests`);
    setGuests(res.data);
  };

  const copyInvite = () => {
    navigator.clipboard.writeText(`${active.invite_code}`);
    toast.success("Invite code copied");
  };

  if (!active && streams.length === 0 && false /* superseded by the !active branch below — kept short-circuited for safety */) {
    return null;
  }

  // No active stream → ALWAYS show the create form + past-streams list
  // first. Previously the page rendered a confusing "Select a stream…"
  // when the host had old streams in their account, blocking the create
  // path. Now the host always sees: "Title your live + Start" up top, and
  // their past/scheduled streams below.
  if (!active) {
    return (
      <div data-testid="directors-mode-empty" className="pt-24 lg:pt-32 pb-20 px-4 sm:px-8 md:px-12 max-w-3xl mx-auto text-center">
        <div className="text-xs uppercase tracking-[0.3em] text-tv-accent font-semibold">Director's Mode</div>
        <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-white tracking-tighter mt-2">Go Live on Tap In TV</h1>
        <p className="text-white/60 text-sm sm:text-base mt-3 sm:mt-4 px-2">Title your stream, pick a category, and tap Start. Your camera opens once you go live.</p>

        <div className="tv-glass rounded-2xl p-4 sm:p-6 mt-6 sm:mt-8 text-left space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-white/60 font-semibold">Stream title <span className="text-tv-live">*</span></label>
            <input
              data-testid="stream-title-input"
              value={newStreamTitle}
              onChange={(e) => setNewStreamTitle(e.target.value)}
              placeholder="e.g. Sunday Gospel Session"
              className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white outline-none focus:border-tv-brand"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-white/60 font-semibold">Genre / category</label>
            <select
              data-testid="live-genre-select"
              value={newStreamGenre}
              onChange={(e) => setNewStreamGenre(e.target.value)}
              className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white outline-none focus:border-tv-brand"
            >
              {genres.map((g) => (<option key={g.slug} value={g.slug}>{g.label}</option>))}
            </select>
          </div>
          <ThumbnailPicker value={newStreamThumb} onChange={setNewStreamThumb} />

          <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
            <button data-testid="start-stream-btn" onClick={startStream} disabled={!newStreamTitle.trim()} className="bg-tv-live hover:bg-red-600 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold px-5 sm:px-6 py-3 rounded-full inline-flex items-center justify-center gap-2 shadow-lg shadow-red-500/20 w-full sm:w-auto">
              <Radio className="w-4 h-4" /> Start Now
            </button>
            <div className="flex-1 flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
              <input
                type="datetime-local"
                value={scheduleDate}
                onChange={(e) => setScheduleDate(e.target.value)}
                data-testid="schedule-date-input"
                className="bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white outline-none focus:border-tv-accent flex-1"
              />
              <button
                data-testid="schedule-stream-btn"
                onClick={scheduleStream}
                disabled={!newStreamTitle.trim() || !scheduleDate}
                className="bg-tv-accent hover:bg-yellow-400 disabled:opacity-40 disabled:cursor-not-allowed text-black font-bold px-5 py-3 rounded-full inline-flex items-center justify-center gap-2"
              >Schedule</button>
            </div>
          </div>
        </div>

        {/* Resume / pick a past stream */}
        {streams.length > 0 && (
          <div data-testid="past-streams-list" className="mt-10 text-left tv-glass rounded-2xl p-5 border border-white/10">
            <div className="text-xs uppercase tracking-[0.2em] text-white/60 font-black mb-3">Pick up where you left off</div>
            <ul className="space-y-2">
              {streams.slice(0, 6).map((s) => (
                <li
                  key={s.id}
                  data-testid={`past-stream-${s.id}`}
                  className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl p-3"
                >
                  <div className="w-12 h-12 rounded-lg overflow-hidden bg-white/10 shrink-0">
                    {s.thumbnail_url && <img src={s.thumbnail_url} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-white truncate">{s.title}</div>
                    <div className="text-[11px] text-white/50">
                      {s.is_live ? <span className="text-tv-live font-black">● LIVE NOW</span> : "Ended"}
                    </div>
                  </div>
                  <button
                    data-testid={`past-stream-open-${s.id}`}
                    onClick={() => setActive(s)}
                    className={`text-xs font-bold px-3 py-1.5 rounded-full inline-flex items-center gap-1 ${s.is_live ? "bg-tv-live text-white" : "bg-white/10 text-white hover:bg-white/20"}`}
                  >
                    {s.is_live ? "Resume" : "Open"}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Scheduled streams */}
        {streams.filter((s) => !s.is_live && s.status === "scheduled").length > 0 && (
          <div data-testid="scheduled-streams-list" className="mt-6 text-left tv-glass rounded-2xl p-5 border border-tv-accent/25">
            <div className="text-xs uppercase tracking-[0.2em] text-tv-accent font-black mb-3">Your scheduled streams</div>
            <ul className="space-y-2">
              {streams.filter((s) => !s.is_live && s.status === "scheduled").map((s) => (
                <li key={s.id} data-testid={`scheduled-stream-${s.id}`} className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl p-3">
                  <div className="w-12 h-12 rounded-lg overflow-hidden bg-white/10 shrink-0">
                    {s.thumbnail_url && <img src={s.thumbnail_url} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-white truncate">{s.title}</div>
                    <div className="text-[11px] text-white/50">{s.scheduled_for ? new Date(s.scheduled_for).toLocaleString() : "No date"}</div>
                  </div>
                  <button data-testid={`scheduled-stream-go-live-${s.id}`} onClick={() => goLiveScheduled(s)} className="bg-tv-live hover:bg-red-600 text-white text-xs font-bold px-3 py-1.5 rounded-full inline-flex items-center gap-1">
                    <Radio className="w-3.5 h-3.5" /> Go live
                  </button>
                  <button data-testid={`scheduled-stream-cancel-${s.id}`} onClick={() => cancelScheduled(s)} className="text-white/50 hover:text-red-300 text-xs font-bold px-2 py-1.5" title="Cancel">
                    <X className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }

  // (legacy) — when streams.length===0 the previous code returned its own
  // empty-state JSX here. We've consolidated everything into the !active
  // branch above so a host who's never streamed AND a host with old
  // streams see the same Title-first flow.

  return (
    <div data-testid="directors-mode" className="pt-16 lg:pt-32 pb-44 lg:pb-0 bg-black min-h-screen overflow-x-hidden">
      {/* Sticky control bar — wraps on mobile, never overflows. Primary
          actions stay visible; secondary actions tuck behind a "More"
          dropdown so the title + LIVE pill + End Stream are always
          one tap away on small screens. */}
      <div className="sticky top-16 z-30 bg-black/85 backdrop-blur-md border-b border-white/10 hidden lg:block">
        <div className="px-3 sm:px-6 py-2.5 flex items-center gap-2 flex-wrap">
          <Circle className="w-3 h-3 fill-tv-live text-tv-live tv-pulse shrink-0" />
          <div className="font-display font-bold text-white truncate max-w-[40vw] sm:max-w-none" title={active.title}>{active.title}</div>
          <div className="hidden md:block text-xs uppercase tracking-wider text-white/40">
            Scene · <span className="text-tv-brand">{active.scene}</span>
          </div>
          <div className="flex-1" />

          {/* Primary, ALWAYS-visible actions — title + 3 essentials */}
          <div className="tv-glass px-2.5 py-1 rounded-full text-[11px] text-white font-semibold flex items-center gap-1.5 shrink-0">
            <Users className="w-3 h-3" /> {viewerCount.toLocaleString()}
          </div>
          <button data-testid="stop-stream-btn" onClick={stopStream} className="bg-tv-live hover:bg-red-600 text-white font-black uppercase tracking-wider text-[10px] sm:text-xs px-3 py-1.5 rounded-full flex items-center gap-1.5 shrink-0">
            <Square className="w-3 h-3 fill-current" /> End
          </button>

          {/* Secondary actions — visible on ≥sm, tucked into More menu on phones */}
          <div className="hidden sm:flex items-center gap-2 flex-wrap">
            <button data-testid="invite-code-btn" onClick={copyInvite} className="tv-glass px-2.5 py-1 rounded-full text-[11px] text-white font-semibold flex items-center gap-1.5 hover:bg-white/10">
              <Copy className="w-3 h-3" /> {active.invite_code}
            </button>
            <button
              data-testid="director-comments-toggle"
              onClick={toggleComments}
              aria-pressed={!commentsEnabled}
              className={`px-2.5 py-1 rounded-full text-[11px] font-semibold flex items-center gap-1.5 transition border ${
                commentsEnabled ? "tv-glass text-white hover:bg-white/10 border-white/10" : "bg-red-500/20 text-red-200 border-red-500/50"
              }`}
            >
              <MessageSquare className="w-3 h-3" /> {commentsEnabled ? "On" : "Off"}
            </button>
            <button
              data-testid="chat-collapse-toggle"
              onClick={() => setChatCollapsed((v) => !v)}
              className="tv-glass px-2.5 py-1 rounded-full text-[11px] text-white font-semibold flex items-center gap-1.5 hover:bg-white/10"
            >
              {chatCollapsed ? "◂ Chat" : "▸ Chat"}
            </button>
            <a
              data-testid="enter-console-btn"
              href={`/studio/console/${active.id}`}
              className="bg-tv-accent hover:bg-yellow-400 text-black font-black uppercase tracking-wider px-3 py-1.5 rounded-full flex items-center gap-1.5 text-[10px] shadow-[0_0_20px_rgba(255,212,0,0.35)]"
              title="Full-screen production console"
            >⛶ Full Studio</a>
            <a
              data-testid="open-cockpit-btn"
              href={`/studio/assist/${active.id}`}
              target="_blank"
              rel="noreferrer"
              className="tv-glass hover:bg-white/10 text-white font-bold uppercase tracking-wider px-3 py-1.5 rounded-full flex items-center gap-1.5 text-[10px] border border-white/10"
              title="Open Assistant Director cockpit (camera stays on this device)"
            >🎛️ Cockpit</a>
          </div>

          {/* Mobile-only "More" overflow */}
          <DirectorMoreMenu
            stream={active}
            commentsEnabled={commentsEnabled}
            chatCollapsed={chatCollapsed}
            onCopyInvite={copyInvite}
            onToggleComments={toggleComments}
            onToggleChat={() => setChatCollapsed((v) => !v)}
          />
        </div>
      </div>

      {/* Mobile sticky preview — always visible at top of mobile viewport
          regardless of which tab is active, so the host never loses
          sight of their camera feed. Hidden on lg+ desktop where the
          existing two-column layout already shows the preview inline. */}
      {/* Mobile live studio — TikTok-style full-screen takeover that
          subsumes the old sticky preview + bottom nav on phones once
          the host has chosen a stream. Hidden while the user is on
          another tab (Mix / Look / Guests / Chat) so the underlying
          panel is reachable — when they're done they tap the floating
          "Stream" pill in the BottomNav to come back.
          Desktop (lg+) still uses the two-column DirectorsMode layout
          and the AdvancedDirectorPanel inline preview, untouched. */}
      {/* Mobile live studio — TikTok-style full-screen takeover that
          subsumes the old sticky preview + bottom nav on phones once
          the host has chosen a stream. Hidden while the user is on
          another tab (Mix / Look / Guests / Chat) so the underlying
          panel is reachable — when they're done they tap the floating
          "Stream" pill in the BottomNav to come back.
          Desktop (lg+) still uses the two-column DirectorsMode layout
          and the AdvancedDirectorPanel inline preview, untouched. */}
      {/* Floating "Back to Studio" pill — appears when the user has
          dimmed the takeover via one of the side-rail destinations and
          their broadcast is still rolling. One-tap brings the TikTok
          studio back up so they don't have to navigate via the
          bottom nav. Mobile only. */}
      {isMobileFlow && active && !mobileStudioOpen && (
        <button
          type="button"
          onClick={() => { setMobileTab("broadcast"); setMobileStudioOpen(true); }}
          data-testid="back-to-studio-pill"
          aria-label="Back to Studio"
          className="fixed left-1/2 -translate-x-1/2 bottom-[88px] z-30 lg:hidden inline-flex items-center gap-1.5 bg-tv-live hover:bg-red-600 text-white font-display font-black uppercase tracking-widest text-[11px] px-4 py-2 rounded-full shadow-[0_8px_24px_rgba(220,38,38,0.45)] border border-red-400/40"
        >
          <Radio className="w-3.5 h-3.5" /> Back to Studio
        </button>
      )}
      {isMobileFlow && active && mobileStudioOpen && mobileTab === "broadcast" ? (
        <MobileLiveStudio
          active={active}
          broadcasterProxy={broadcasterProxy}
          webrtcMode={webrtcMode}
          viewerCount={viewerCount}
          followerCount={active?.creator_follower_count}
          chat={chat}
          guests={guests}
          fxSettings={fxSettings}
          onStartCamera={() => setWebrtcMode(true)}
          onGoLive={() => broadcasterProxy?.startBroadcast?.()}
          onEndCamera={() => setWebrtcMode(false)}
          onEndStream={stopStream}
          onSwitchTab={(t) => { setMobileTab(t); setMobileStudioOpen(false); }}
          onOpenFx={() => setFxOpen(true)}
          onOpenSceneSwitcher={() => { setMobileTab("overlays"); setMobileStudioOpen(false); }}
          onOpenCameraSwitcher={() => { setMobileTab("broadcast"); setMobileStudioOpen(false); }}
          onOpenOverlays={() => { setMobileTab("overlays"); setMobileStudioOpen(false); }}
          onOpenLayoutPicker={() => { setMobileTab("overlays"); setMobileStudioOpen(false); }}
          onOpenSettings={() => { setMobileTab("broadcast"); setMobileStudioOpen(false); }}
          onOpenTutorial={() => setTutorialOpen(true)}
          onSendComment={async (body) => {
            if (active?.id) await api.post(`/live/${active.id}/chat`, { message: body });
          }}
          onSendTip={() => { setMobileTab("chat"); setMobileStudioOpen(false); }}
          onClose={stopStream}
        />
      ) : (
        <MobileLivePreviewBar
          active={active}
          broadcasterProxy={broadcasterProxy}
          webrtcMode={webrtcMode}
          onStartCamera={() => setWebrtcMode(true)}
          onEndCamera={() => setWebrtcMode(false)}
          onEndStream={stopStream}
          viewerCount={viewerCount}
          onSwitchTab={setMobileTab}
        />
      )}

      {/* Floating bottom nav — single navigation surface for mobile.
          Previously a top MobileTabBar lived here too; removed because
          having both a top pill row AND a bottom nav cluttered the
          mobile layout with duplicate navigation. Bottom nav is more
          thumb-friendly and iOS/Android-native.
          Pinned above the iOS safe-area inset; hidden on lg+ desktop.
          End-stream + flip + mic actions live in the sticky top preview
          header (or in context within each tab).
          The grab handle above the nav + swipe-up gesture reveals a
          Quick-action sheet with shortcuts that would otherwise
          require tab-switching (Mic toggle, Cam flip, GS jump, End). */}
      <MobileBottomNav
        tab={mobileTab}
        setTab={setMobileTab}
        testid="directors-mode-bottom-nav"
        tabs={[
          { id: "broadcast", label: "Stream",  icon: VideoIcon },
          { id: "audio",     label: "Mix",     icon: Sliders },
          { id: "overlays",  label: "Look",    icon: ImageIcon },
          { id: "guests",    label: "Guests",  icon: Users, badge: guests?.length || 0 },
          { id: "chat",      label: "Chat",    icon: MessageSquare, badge: chat?.length || 0 },
        ]}
        quickActions={webrtcMode ? [
          {
            id: "mic",
            label: micMuted ? "Muted" : "Mic",
            icon: micMuted ? MicOff : Mic,
            onClick: handleToggleMic,
            active: micMuted,
            danger: true,
            keepOpen: true,
          },
          {
            id: "flip",
            label: "Flip Cam",
            icon: RefreshCcw,
            onClick: () => broadcasterProxy?.flipCamera?.(),
          },
          {
            id: "gs",
            label: "Green Screen",
            icon: Scissors,
            onClick: () => setMobileTab("broadcast"),
          },
          {
            id: "end",
            label: "End Live",
            icon: Square,
            onClick: stopStream,
            danger: true,
            active: true,
          },
        ] : null}
      />

      <div className="grid grid-cols-12 gap-4 px-4 md:px-6 pb-8">
        {/* Preview + controls — center when chat collapsed */}
        <div className={chatCollapsed ? "col-span-12 lg:col-span-12 xl:col-start-2 xl:col-span-10" : "col-span-12 lg:col-span-8"}>
          <MobileTabSection tab={mobileTab} match="broadcast">
          {/* Real-time viewer count + share-to-social */}
          {active?.is_live && (
            <div className="mb-3 space-y-2">
              <GoLiveShareBar streamId={active.id} title={active.title} />
              <AiThumbnailPicker
                streamId={active.id}
                currentThumbnail={active.thumbnail_url}
                autoIntervalSec={120}
                onChosen={(url) => setActive((a) => a ? { ...a, thumbnail_url: url } : a)}
              />
              {(active?.clips_shared_count || 0) > 0 && (
                <div data-testid="live-clip-count-badge" className="inline-flex items-center gap-1.5 bg-cyan-500/15 border border-cyan-400/30 text-cyan-200 text-[11px] font-black uppercase tracking-[0.18em] px-3 py-1 rounded-full">
                  ✂️ {active.clips_shared_count} viewer clip{active.clips_shared_count === 1 ? "" : "s"} shared
                </div>
              )}
            </div>
          )}
          <div className="mb-3 hidden lg:flex items-center gap-2 flex-wrap">
            <button
              data-testid="toggle-webrtc-broadcast"
              onClick={() => setWebrtcMode((m) => !m)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 ${webrtcMode ? "bg-tv-live text-white" : "bg-white/10 text-white hover:bg-white/20"}`}
            >
              <VideoIcon className="w-3.5 h-3.5" /> {webrtcMode ? "Stop camera broadcast" : "Go live with camera (WebRTC)"}
            </button>
            <div className="text-[11px] text-white/50">WebRTC broadcasts your camera + mic directly to Tap In TV viewers in real time.</div>
          </div>

          {/* When broadcasting, the MobileLivePreviewBar at the top is
              the persistent camera surface — the Stream tab on mobile
              now houses the broadcaster panel directly. The CTA below
              still surfaces on mobile only as a quick jump to the audio
              mixer when the user is hunting for FX. */}
          {webrtcMode && (
            <button
              type="button"
              onClick={() => setMobileTab("audio")}
              data-testid="goto-audio-tab"
              className="lg:hidden w-full bg-tv-accent/10 border border-tv-accent/40 rounded-xl p-2.5 text-left text-[11px] text-tv-accent font-bold flex items-center gap-2 mb-3"
            >
              <Sliders className="w-3.5 h-3.5" /> Open Mix tab for EQ + FX →
            </button>
          )}

          {!webrtcMode && (
          <div data-testid="stream-preview" className="hidden lg:block relative aspect-video rounded-xl overflow-hidden bg-[#0B1120] border border-white/10">
            <img src={active.thumbnail_url} alt="" className="w-full h-full object-cover opacity-60" />
            <div className="absolute inset-0 flex items-center justify-center flex-col">
              <div className="bg-black/70 px-5 py-3 rounded-lg text-white font-display text-2xl">
                Scene: <span className="text-tv-brand uppercase">{active.scene}</span>
              </div>
              <div className="mt-2 text-xs uppercase tracking-[0.3em] text-tv-accent">Simulated preview</div>
            </div>
            <div className="absolute top-3 left-3 flex items-center gap-2">
              <span className="bg-tv-live text-white text-[10px] font-bold uppercase px-2 py-1 rounded flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-white tv-pulse" /> ON AIR
              </span>
            </div>
            {active.overlays?.lower_third && (
              <div className="absolute bottom-4 left-4 bg-gradient-to-r from-tv-accent to-yellow-500 text-black px-4 py-1.5 rounded">
                <div className="text-[10px] uppercase tracking-wider font-bold opacity-70">Tap In TV</div>
                <div className="font-display font-black">{active.overlays.lower_third}</div>
              </div>
            )}
            {/* QR overlay preview — shows the host where the QR will land
                on the broadcast frame so they can adjust position before
                going live. The same overlay renders on the listener-side
                Watch.jsx player. */}
            <QrOverlayLayer overlays={active.overlays} />
          </div>
          )}
          </MobileTabSection>

          {/* AdvancedDirectorPanel mounts ALWAYS when broadcasting —
              regardless of which tab is active. Without this, the
              camera engine was previously tied to the "Audio" tab,
              causing the user-reported "camera starts in Audio" bug.
              We render it on the Stream tab (where it belongs) and
              keep its identity/state stable across tab switches.
              On the Mix tab, the same panel is hidden (display:none)
              so we don't unmount and reset the camera + WebRTC peers
              when the host pops over to tweak EQ/FX. */}
          {webrtcMode && (
            <div className={mobileTab === "broadcast" ? "block" : "hidden"}>
              <AdvancedDirectorPanel
                streamId={active.id}
                kidsBrand={active.category === "kids"}
                onEnd={() => setWebrtcMode(false)}
                onBroadcasterReady={setBroadcasterProxy}
                onAudioReady={({ audioCtx, outputDest }) => setAudioPipe({ ctx: audioCtx, dest: outputDest })}
                fxSettings={fxSettings}
                hideVideoPreview={isMobileFlow}
              />
            </div>
          )}

          {/* Audio Mix tab — JUST the mixer rack. The broadcaster panel
              is mounted up on the Stream tab (always-on), so the host
              can pop into Mix to tweak FX without tearing down camera. */}
          <MobileTabSection tab={mobileTab} match="audio">
            {webrtcMode ? (
              <DirectorMixerRack
                open={mixerOpen}
                onToggle={() => setMixerOpen((o) => !o)}
                audioCtx={audioPipe.ctx}
                outputDest={audioPipe.dest}
                guests={liveGuests}
                floating={mixerFloat}
                onToggleFloat={() => setMixerFloat((f) => !f)}
                broadcasterProxy={broadcasterProxy}
              />
            ) : (
              <div data-testid="audio-tab-empty" className="rounded-xl border border-dashed border-white/20 bg-white/[0.02] p-6 text-center">
                <Sliders className="w-8 h-8 text-tv-accent/50 mx-auto mb-2" />
                <div className="font-display font-bold text-white text-base mb-1">Audio rack & FX</div>
                <p className="text-white/55 text-xs leading-relaxed mb-3">
                  Start your camera on the Stream tab to power up the mixer. You'll get per-channel faders, FX (echo, reverb, telephone, robot…), a 2nd-mic input, and the soundboard.
                </p>
                <button
                  type="button"
                  onClick={() => { setMobileTab("broadcast"); setWebrtcMode(true); }}
                  data-testid="audio-tab-empty-cta"
                  className="bg-tv-live hover:bg-red-600 text-white font-black uppercase tracking-wider text-[10px] px-3 py-1.5 rounded-full"
                >Start camera broadcast</button>
              </div>
            )}
          </MobileTabSection>

          <MobileTabSection tab={mobileTab} match="broadcast">
          <div className="mt-4 tv-glass rounded-xl p-4">
            <div className="flex items-center gap-2 text-white font-display font-bold"><Layers className="w-4 h-4" /> Scene Switcher</div>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
              {SCENES.map((s) => (
                <button
                  key={s.id}
                  data-testid={`scene-${s.id}`}
                  onClick={() => switchScene(s.id)}
                  className={`px-3 py-3 rounded-lg text-sm font-semibold border transition ${active.scene === s.id ? "border-tv-live bg-tv-live/10 text-white" : "border-white/10 text-white/70 hover:bg-white/5"}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
          </MobileTabSection>

          <MobileTabSection tab={mobileTab} match="overlays">
          <div className="mt-4 tv-glass rounded-xl p-4 flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-white font-display font-bold mr-2"><Megaphone className="w-4 h-4" /> Monetization</div>
            <button data-testid="trigger-ad-btn" onClick={triggerAd} className="bg-tv-accent hover:bg-yellow-400 text-black font-bold px-4 py-2 rounded-full text-sm flex items-center gap-2">
              <Zap className="w-4 h-4" /> Trigger 15s Ad
            </button>
            <div className="text-xs text-white/50">Sell your own slots via the Ad Inventory manager (coming soon).</div>
          </div>

          <div className="mt-4 tv-glass rounded-xl p-4 flex items-center gap-3 flex-wrap" data-testid="directorsmode-multistream-hint">
            <div className="flex items-center gap-2 text-white font-display font-bold mr-2"><Radio className="w-4 h-4" /> Multistream to socials</div>
            <button
              data-testid="directorsmode-open-console"
              onClick={() => window.location.assign(`/studio/console/${active.id}`)}
              className="bg-fuchsia-500 hover:bg-fuchsia-400 text-black font-bold px-4 py-2 rounded-full text-sm flex items-center gap-2"
            >
              Open Director Console →
            </button>
            <div className="text-xs text-white/50">The full console has multi-camera switching, scene compositor, and "Go live to YouTube / Facebook / Twitch / Kick / Restream" built in.</div>
          </div>
          </MobileTabSection>

          <MobileTabSection tab={mobileTab} match="guests">
          {/* Phone camera pairing — show a QR the host scans to use their
              phone as a second cam (selfie or back). The stream lands as an
              extra video source the host can switch into via the scene
              picker above. Mount unconditionally so the host can pair a
              phone before going live (idle stream is fine). */}
          <div className="mt-4">
            <PhoneCameraPanel
              onStream={() => toast.success("Phone camera connected. Open Full Studio to switch to it as a scene.")}
              onEnded={() => toast.info("Phone camera disconnected.")}
            />
          </div>

          {/* Invite Tap In creators on-air — the GuestManager runs the
              full waiting-room + invite-by-username flow. Mount only when
              there's an active stream because the invite endpoint needs
              a stream id. */}
          <div className="mt-4">
            <GuestManager
              streamId={active.id}
              inviteCode={active.invite_code}
              guests={guests}
              onRefresh={() => api.get(`/live/${active.id}/guests`).then((r) => setGuests(r.data || [])).catch(() => {})}
            />
          </div>
          </MobileTabSection>

          <MobileTabSection tab={mobileTab} match="overlays">
          {/* QR overlay — host pins a payment / call-to-action QR on the
              stream that listeners can scan. Renders bottom-left/center/right
              of the broadcast frame. */}
          <div className="mt-4">
            <QrOverlayPanel
              streamId={active.id}
              currentOverlays={active.overlays}
            />
          </div>
          </MobileTabSection>
        </div>

        {/* Right sidebar: chat + guests (collapsible) */}
        {!chatCollapsed && (
        <div className="col-span-12 lg:col-span-4 flex-col gap-4 lg:flex">
          <MobileTabSection tab={mobileTab} match="chat">
          <div className="tv-glass rounded-xl p-4 flex flex-col h-[340px]">
            <div className="flex items-center gap-2 text-white font-display font-bold mb-3"><MessageSquare className="w-4 h-4" /> Live Chat</div>
            <div ref={chatRef} data-testid="live-chat" className="flex-1 overflow-y-auto space-y-2 text-sm pr-1">
              {chat.length === 0 && <div className="text-white/40 text-xs">No messages yet.</div>}
              {chat.map((m) => (
                <div key={m.id} className="flex gap-2 items-start" data-testid={`chat-msg-${m.id}`}>
                  <div className="w-6 h-6 rounded-full bg-tv-brand text-black font-bold flex items-center justify-center text-xs shrink-0">{m.user_name?.[0]}</div>
                  <div>
                    <span className="text-tv-brand font-semibold text-xs">{m.user_name}</span>{" "}
                    <span className="text-white/80">{m.message}</span>
                  </div>
                </div>
              ))}
            </div>
            <form onSubmit={sendChat} className="mt-3 flex gap-2">
              <input data-testid="chat-input" value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Message the stream" className="flex-1 bg-white/5 border border-white/10 rounded-full px-3 py-2 text-sm text-white outline-none" />
              <button data-testid="chat-send-btn" className="bg-tv-accent text-black font-bold px-4 rounded-full text-sm">Send</button>
            </form>
          </div>
          </MobileTabSection>

          <MobileTabSection tab={mobileTab} match="guests">
          <div className="tv-glass rounded-xl p-4 mt-4 lg:mt-0">
            <div className="flex items-center gap-2 text-white font-display font-bold mb-3"><Users className="w-4 h-4" /> Backstage · Guests</div>
            <div className="text-xs text-white/50 mb-3">Share your invite code <span className="text-tv-brand font-semibold">{active.invite_code}</span> for guests to join.</div>
            <div className="space-y-2 max-h-56 overflow-y-auto">
              {guests.length === 0 && <div className="text-white/40 text-xs">No guests waiting.</div>}
              {guests.map((g) => (
                <div key={g.id} className="flex items-center gap-2" data-testid={`guest-${g.id}`}>
                  <div className="w-7 h-7 rounded-full bg-white/10 text-white font-bold text-xs flex items-center justify-center">{g.user_name?.[0]}</div>
                  <div className="flex-1">
                    <div className="text-white text-sm">{g.user_name}</div>
                    <div className="text-[10px] uppercase tracking-wider text-white/50">{g.status}</div>
                  </div>
                  <button onClick={() => decide(g, "spotlight")} data-testid={`guest-spotlight-${g.id}`} className="text-xs text-tv-brand">On Air</button>
                  <button onClick={() => decide(g, "remove")} data-testid={`guest-remove-${g.id}`} className="text-xs text-red-400">Remove</button>
                </div>
              ))}
            </div>
          </div>
          </MobileTabSection>
        </div>
        )}
      </div>
      <EndStreamPrompt
        open={!!endPrompt}
        stream={endPrompt}
        busy={endBusy}
        onAction={finishStream}
        onClose={() => setEndPrompt(null)}
      />
      <StudioFxModal
        open={fxOpen}
        onClose={() => setFxOpen(false)}
        onApply={(s) => setFxSettings(s)}
        initial={fxSettings}
        streamId={active?.id}
      />
      <GoLiveTutorial open={tutorialOpen} onClose={() => setTutorialOpen(false)} />
    </div>
  );
}

function EndStreamPrompt({ open, stream, busy, onAction, onClose }) {
  const [recap, setRecap] = useState("");
  useEffect(() => {
    if (open && stream) setRecap(`Just wrapped "${stream.title}" — thanks to everyone who tuned in!`);
  }, [open, stream]);
  if (!open || !stream) return null;
  const actions = [
    { key: "archive", label: "Archive as Replay", desc: "Save the broadcast to your library so fans can watch on demand.", Icon: Archive, cta: "bg-tv-accent text-black hover:bg-yellow-400" },
    { key: "post_to_feed", label: "Post Recap to Community", desc: "Share a wrap-up post on your Community Wall.", Icon: MessageCircle, cta: "bg-white/10 text-white hover:bg-white/20 border border-white/20" },
    { key: "save_draft", label: "Save as Draft", desc: "Keep the record private until you decide what to do.", Icon: Save, cta: "bg-white/10 text-white hover:bg-white/20 border border-white/20" },
    { key: "delete", label: "Delete", desc: "Permanently remove this stream and its chat history.", Icon: Trash2, cta: "bg-red-500/20 text-red-300 hover:bg-red-500/30 border border-red-500/40" },
  ];
  return (
    <div data-testid="end-stream-prompt" className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-[#0B1120] border border-white/10 rounded-2xl w-full max-w-xl p-6 relative">
        <button
          data-testid="end-stream-close"
          onClick={onClose}
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 text-white flex items-center justify-center"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="text-xs uppercase tracking-[0.3em] text-tv-accent font-bold">Broadcast ended</div>
        <h3 className="font-display font-black text-3xl text-white mt-2 tracking-tighter">What should we do with the recording?</h3>
        <p className="text-white/60 text-sm mt-2">Pick what happens next for <b className="text-white">{stream.title}</b>.</p>

        {(stream.clips_shared_count || 0) > 0 && (
          <div
            data-testid="end-stream-clips-shared"
            className="mt-4 flex items-center gap-3 bg-gradient-to-r from-cyan-500/15 via-blue-500/15 to-indigo-500/15 border border-cyan-400/30 rounded-xl px-4 py-3"
          >
            <Scissors className="w-5 h-5 text-cyan-300 shrink-0" />
            <div className="flex-1">
              <div className="text-cyan-100 text-sm font-bold">
                <span data-testid="end-stream-clips-count">{stream.clips_shared_count}</span> viewer{stream.clips_shared_count === 1 ? " " : "s "}
                turned this broadcast into a clip
              </div>
              <div className="text-cyan-200/70 text-[11px]">Posting the recap surfaces those clips to the same fans for replay.</div>
            </div>
          </div>
        )}

        <label className="block mt-5">
          <span className="text-[10px] uppercase tracking-wider text-white/50 font-bold">Recap caption (for community post)</span>
          <textarea
            data-testid="end-stream-recap-input"
            value={recap}
            onChange={(e) => setRecap(e.target.value)}
            rows={2}
            className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-tv-accent resize-none"
          />
        </label>

        <div className="mt-5 grid gap-3">
          {actions.map(({ key, label, desc, Icon, cta }) => (
            <button
              key={key}
              data-testid={`end-stream-${key}`}
              onClick={() => onAction(key, key === "post_to_feed" ? { content: recap } : {})}
              disabled={!!busy}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl text-left transition ${cta} disabled:opacity-60`}
            >
              <Icon className="w-5 h-5 shrink-0" />
              <div className="flex-1">
                <div className="font-bold text-sm">{busy === key ? "Working…" : label}</div>
                <div className="text-[11px] opacity-80 font-normal">{desc}</div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}


/**
 * DirectorMoreMenu — phone-only overflow menu for the active-stream
 * control bar. On screens ≥sm the secondary buttons (invite code,
 * comments toggle, chat hide, Full Studio, Cockpit) live inline; on
 * phones they fold into this dropdown so the bar never overlaps with
 * the title or the End-Stream CTA.
 */
function DirectorMoreMenu({ stream, commentsEnabled, chatCollapsed, onCopyInvite, onToggleComments, onToggleChat }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative sm:hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="director-more-btn"
        className="tv-glass px-2.5 py-1 rounded-full text-[11px] text-white font-bold flex items-center gap-1 hover:bg-white/10"
        aria-expanded={open}
      >⋯ More</button>
      {open && (
        <div data-testid="director-more-menu" className="absolute right-0 top-full mt-1 w-56 bg-[#0B0F16] border border-white/10 rounded-lg shadow-2xl z-40 p-1.5">
          <button
            type="button"
            onClick={() => { onCopyInvite?.(); setOpen(false); }}
            data-testid="director-more-invite"
            className="w-full text-left px-3 py-2 text-[12px] text-white/85 hover:bg-white/5 rounded inline-flex items-center gap-2"
          ><Copy className="w-3.5 h-3.5" /> Copy invite — {stream.invite_code}</button>
          <button
            type="button"
            onClick={() => { onToggleComments?.(); setOpen(false); }}
            data-testid="director-more-comments"
            className="w-full text-left px-3 py-2 text-[12px] text-white/85 hover:bg-white/5 rounded inline-flex items-center gap-2"
          ><MessageSquare className="w-3.5 h-3.5" /> Comments — {commentsEnabled ? "On" : "Off"}</button>
          <button
            type="button"
            onClick={() => { onToggleChat?.(); setOpen(false); }}
            data-testid="director-more-chat"
            className="w-full text-left px-3 py-2 text-[12px] text-white/85 hover:bg-white/5 rounded"
          >{chatCollapsed ? "◂ Show chat sidebar" : "▸ Hide chat sidebar"}</button>
          <a
            href={`/studio/console/${stream.id}`}
            data-testid="director-more-fullstudio"
            className="block px-3 py-2 text-[12px] text-tv-accent font-bold hover:bg-white/5 rounded"
          >⛶ Full Studio</a>
          <a
            href={`/studio/assist/${stream.id}`}
            target="_blank"
            rel="noreferrer"
            data-testid="director-more-cockpit"
            className="block px-3 py-2 text-[12px] text-white font-bold hover:bg-white/5 rounded"
          >🎛️ Open Cockpit (new tab)</a>
        </div>
      )}
    </div>
  );
}
